import { pushEvent, trackifyPageChange, trackifyInit } from '@allianz/tracking';
import { HttpErrorResponse, HTTP_INTERCEPTORS } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import { throwError, Subject } from 'rxjs';
import { catchError, takeUntil, filter, debounceTime } from 'rxjs/operators';
import * as i1 from '@allianz/ngx-pfe';
import { PfeUtilService, NGX_PFE_CONFIGURATION, NgxPfeModule } from '@allianz/ngx-pfe';
import { __awaiter } from 'tslib';

class TrackingHttpInterceptorService {
    intercept(request, next) {
        return next.handle(request).pipe(catchError((response) => {
            if (response instanceof HttpErrorResponse) {
                const httpError = {
                    url: request.url,
                    status: response.status,
                    statusText: response.statusText,
                    name: response.name,
                    message: response.message,
                };
                pushEvent('httpError', httpError, true);
            }
            return throwError(response);
        }));
    }
}
TrackingHttpInterceptorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: TrackingHttpInterceptorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TrackingHttpInterceptorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: TrackingHttpInterceptorService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: TrackingHttpInterceptorService, decorators: [{
            type: Injectable
        }] });

/**
 * The PfeTrackingService makes it easier to use the Tracking Library
 * (https://github.developer.allianz.io/ilt/tracking) in combination with the pfe.
 *
 * It automatically initializes trackify with the configuration from the NgxPfeModuleConfiguration.
 * Page change events are automatically pushed.
 *
 * The tracking service also provides the functionality to track values in the state.
 * This can be configured in the appConfiguration.
 *
 */
class PfeTrackingService {
    constructor(ngxPfeModuleConfiguration, pfeBusinessService, pfeNavigationService, pfeConfigurationService, pfeUtilService) {
        this.ngxPfeModuleConfiguration = ngxPfeModuleConfiguration;
        this.pfeBusinessService = pfeBusinessService;
        this.pfeNavigationService = pfeNavigationService;
        this.pfeConfigurationService = pfeConfigurationService;
        this.pfeUtilService = pfeUtilService;
        this.componentDestroyed$ = new Subject();
        this.initialized = false;
        if (this.ngxPfeModuleConfiguration.tracking) {
            this.pfeUtilService.trackingEvents$.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
                this.pushEvent(event.event, event.value, event.ignorePageId);
            });
            this.pfeUtilService.trackingStateRestored$.pipe(takeUntil(this.componentDestroyed$)).subscribe((restoredState) => {
                this.pushStateRestoreTrackingEvent(restoredState);
            });
            this.pfeNavigationService.currentPageId$
                .pipe(takeUntil(this.componentDestroyed$), //
            filter((pageID) => pageID !== undefined))
                .subscribe((pageID) => {
                if (!this.initialized) {
                    this.initialized = true;
                    this.trackifyInit();
                }
                else {
                    this.trackifyPageChange(pageID);
                }
            });
            this.initializeStateValueTracking();
        }
    }
    trackifyPageChange(pageId) {
        if (pageId) {
            trackifyPageChange(pageId);
        }
    }
    ngOnDestroy() {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }
    trackifyInit() {
        var _a, _b, _c, _d;
        const trackifyConfig = {
            production: this.ngxPfeModuleConfiguration.production,
            appName: ((_a = this.ngxPfeModuleConfiguration.tracking) === null || _a === void 0 ? void 0 : _a.appName) || this.ngxPfeModuleConfiguration.applicationID,
            googleId: (_b = this.ngxPfeModuleConfiguration.tracking) === null || _b === void 0 ? void 0 : _b.googleId,
            adobeUrl: (_c = this.ngxPfeModuleConfiguration.tracking) === null || _c === void 0 ? void 0 : _c.adobeUrl,
            customScript: (_d = this.ngxPfeModuleConfiguration.tracking) === null || _d === void 0 ? void 0 : _d.customScript,
            pageId: this.pfeNavigationService.currentPageId$.value,
        };
        trackifyInit(trackifyConfig);
    }
    pushEvent(event, value, ignorePageId) {
        pushEvent(event, value, ignorePageId);
    }
    initializeStateValueTracking() {
        this.pfeConfigurationService.getConfig().then((pfeConfig) => {
            var _a, _b, _c;
            if ((_c = (_b = (_a = pfeConfig === null || pfeConfig === void 0 ? void 0 : pfeConfig.appConfiguration) === null || _a === void 0 ? void 0 : _a.pfeConfig) === null || _b === void 0 ? void 0 : _b.trackingConfig) === null || _c === void 0 ? void 0 : _c.stateTracking) {
                pfeConfig.appConfiguration.pfeConfig.trackingConfig.stateTracking.forEach((stateTrackingConfig) => {
                    const eventName = this.determineStateTrackingEventName(stateTrackingConfig);
                    this.pfeBusinessService
                        .getObservableForExpressionKey(stateTrackingConfig.stateKeyExpression, true)
                        .pipe(debounceTime(200), takeUntil(this.componentDestroyed$))
                        .subscribe((value) => {
                        this.pushEvent(eventName, value, stateTrackingConfig.ignorePageId);
                    });
                });
            }
        });
    }
    pushStateRestoreTrackingEvent(restoredState) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const config = yield this.pfeConfigurationService.getConfig();
            if (!((_c = (_b = (_a = config === null || config === void 0 ? void 0 : config.appConfiguration) === null || _a === void 0 ? void 0 : _a.pfeConfig) === null || _b === void 0 ? void 0 : _b.trackingConfig) === null || _c === void 0 ? void 0 : _c.stateTracking)) {
                return;
            }
            // eslint-disable-next-line @typescript-eslint/ban-types
            const eventData = {};
            config.appConfiguration.pfeConfig.trackingConfig.stateTracking.forEach((stateTrackingConfig) => {
                const eventName = this.determineStateTrackingEventName(stateTrackingConfig);
                const value = PfeUtilService.getValueOrList(restoredState, stateTrackingConfig.stateKeyExpression);
                eventData[eventName] = value;
            });
            this.pushEvent(`${((_d = this.ngxPfeModuleConfiguration.tracking) === null || _d === void 0 ? void 0 : _d.appName) || this.ngxPfeModuleConfiguration.applicationID}.restoringPFEState`, eventData);
        });
    }
    determineStateTrackingEventName(stateTrackingConfig) {
        return stateTrackingConfig.event ? stateTrackingConfig.event : stateTrackingConfig.stateKeyExpression.replace(/^(\$\.)/, '');
    }
}
PfeTrackingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeTrackingService, deps: [{ token: NGX_PFE_CONFIGURATION }, { token: i1.PfeBusinessService }, { token: i1.PfeNavigationService }, { token: i1.PfeConfigurationService }, { token: i1.PfeUtilService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeTrackingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeTrackingService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeTrackingService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [NGX_PFE_CONFIGURATION]
                    }] }, { type: i1.PfeBusinessService }, { type: i1.PfeNavigationService }, { type: i1.PfeConfigurationService }, { type: i1.PfeUtilService }];
    } });

class PfeTrackingModule {
    constructor(pfeTrackingService) {
        this.pfeTrackingService = pfeTrackingService;
    }
}
PfeTrackingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeTrackingModule, deps: [{ token: PfeTrackingService }], target: i0.ɵɵFactoryTarget.NgModule });
PfeTrackingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeTrackingModule, imports: [NgxPfeModule] });
PfeTrackingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeTrackingModule, providers: [
        PfeTrackingService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TrackingHttpInterceptorService,
            multi: true,
        },
    ], imports: [[NgxPfeModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeTrackingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [NgxPfeModule],
                    providers: [
                        PfeTrackingService,
                        {
                            provide: HTTP_INTERCEPTORS,
                            useClass: TrackingHttpInterceptorService,
                            multi: true,
                        },
                    ],
                }]
        }], ctorParameters: function () { return [{ type: PfeTrackingService }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { PfeTrackingModule, PfeTrackingService, TrackingHttpInterceptorService };
